import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/uiiii/Button';
import Input from '../../../components/uiiii/Input';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../api';

const RegisterForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    nom: '',       
    email: '',
    telephone: '',
    adresse: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fonction pour gérer les changements dans les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Réinitialiser les erreurs du champ
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Fonction de validation du formulaire
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nom?.trim()) {
      newErrors.nom = 'Le nom complet est requis';
    }

    if (!formData?.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
      newErrors.email = 'Veuillez entrer un email valide';
    }

    if (!formData?.telephone) {
      newErrors.telephone = 'Le numéro de téléphone est requis';
    } else {
      // Supprimer les espaces et le +216 s'il est présent
      const phone = formData.telephone.replace(/\s+/g, '').replace(/^\+216/, '');
      
      // Vérifier que c'est un numéro à 8 chiffres
      if (!/^\d{8}$/.test(phone)) {
        newErrors.telephone = 'Le numéro doit contenir 8 chiffres';
      } 
      // Vérifier les préfixes valides en Tunisie
      else {
        const prefix = phone.substring(0, 2);
        const validPrefixes = [
          // Ooredoo
          '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '46',
          // Orange (plages limitées)
          '30', '31', '32', '50', '51', '52', '53', '54', '55', '56', '58',
          // Tunisie Telecom
          '40', '41', '42', '44', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99',
          // MVNO
          '43', '45'
        ];
        
        if (!validPrefixes.includes(prefix)) {
          newErrors.telephone = 'Préfixe de numéro non valide en Tunisie';
        }
      }
    }

    if (!formData?.adresse) {
      newErrors.adresse = 'L\'adresse est requise';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Le mot de passe doit comporter au moins 6 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour gérer l'envoi du formulaire
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({}); // Réinitialiser les erreurs précédentes
    
    try {
      // Appel à l'API d'inscription
      const response = await api.privateApi.auth.signup({
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        adresse: formData.adresse,
        password: formData.password
      });
      
      // Vérification de la réponse de l'API
      if (response && response.token && response.user) {
        // Connexion automatique après inscription réussie
        login(response.user, response.token);
        onSuccess?.(response.user);
        
        // Redirection vers la page de bienvenue
        navigate('/new-member-welcome');
      } else {
        // Gestion des réponses inattendues
        setErrors(prev => ({
          ...prev,
          general: response?.message || "Une erreur inattendue s'est produite. Veuillez réessayer."
        }));
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Gestion des erreurs spécifiques
      if (error.response) {
        // Erreur 409 - Conflit (email déjà utilisé)
        if (error.response.status === 409) {
          setErrors(prev => ({
            ...prev,
            email: 'Cette adresse email est déjà utilisée. Essayez de vous connecter ou utilisez une autre adresse email.',
            general: null
          }));
        } 
        // Autres erreurs avec message du serveur
        else if (error.response.data?.message) {
          setErrors(prev => ({
            ...prev,
            general: error.response.data.message
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            general: 'Une erreur est survenue lors de la communication avec le serveur.'
          }));
        }
      } 
      // Erreur de réseau ou autre erreur inattendue
      else {
        setErrors(prev => ({
          ...prev,
          general: 'Impossible de se connecter au serveur. Vérifiez votre connexion et réessayez.'
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Affichage des erreurs générales */}
      {errors.general && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg animate-fade-in">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
            <p className="text-sm text-error">{errors.general}</p>
          </div>
        </div>
      )}

      {/* Affichage des erreurs de validation */}
      {Object.values(errors).some(error => error && typeof error === 'string' && error !== '') && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Veuillez corriger les erreurs suivantes :</p>
              <ul className="list-disc pl-5 text-sm text-foreground/80 space-y-1">
                {Object.entries(errors)
                  .filter(([key, value]) => value && typeof value === 'string' && key !== 'general')
                  .map(([key, value]) => (
                    <li key={key}>
                      {value}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <Input
        label="Nom complet"
        type="text"
        name="nom"
        placeholder="Entrez votre nom complet"
        value={formData?.nom}
        onChange={handleInputChange}
        error={errors?.nom}
        required
      />

      <Input
        label="Adresse Email"
        type="email"
        name="email"
        placeholder="Entrez votre email"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
      />

      <div className="relative">
        <Input
          label="Numéro de téléphone"
          type="tel"
          name="telephone"
          placeholder="Ex: 98 123 456"
          value={formData?.telephone}
          onChange={(e) => {
            // N'autoriser que les chiffres et espaces
            const value = e.target.value.replace(/[^\d\s]/g, '');
            // Limiter à 8 chiffres + espaces
            if (value.replace(/\s/g, '').length <= 8) {
              handleInputChange({
                target: { name: 'telephone', value: value.trim() }
              });
            }
          }}
          error={errors?.telephone}
          required
        />
        <div className="absolute right-3 top-9 text-xs text-muted-foreground">
          +216
        </div>
      </div>

      <Input
        label="Adresse"
        type="text"
        name="adresse"
        placeholder="Entrez votre adresse"
        value={formData?.adresse}
        onChange={handleInputChange}
        error={errors?.adresse}
        required
      />

      <div className="relative">
        <Input
          label="Mot de passe"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="Créez un mot de passe sécurisé"
          value={formData?.password}
          onChange={handleInputChange}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-text-primary transition-colors"
          aria-label={showPassword ? 'Cacher le mot de passe' : 'Afficher le mot de passe'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>

      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="right"
        className="animate-fade-in"
      >
        {isLoading ? 'Création du compte...' : 'Créer un compte'}
      </Button>
    </form>
  );
};

export default RegisterForm;
